import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState, Chip } from "../components/Common";
import { ClipboardList, Activity, ArrowRight, HeartPulse, Droplet, Pill, X, Save } from "lucide-react";
import { toast } from "sonner";
import NurseRounds from "./admitted/NurseRounds";

const ACUITY = {
  1: { label: "1 · Resuscitation", tone: "red" },
  2: { label: "2 · Emergent",       tone: "red" },
  3: { label: "3 · Urgent",         tone: "amber" },
  4: { label: "4 · Less urgent",    tone: "blue" },
  5: { label: "5 · Non-urgent",     tone: "slate" },
};

export default function Nurse() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [tab, setTab] = useState("rounds");
  const [triageFor, setTriageFor] = useState(null); // queue row
  const [triageFilter, setTriageFilter] = useState("all"); // all | waiting | today | critical
  const [onDutyOpen, setOnDutyOpen] = useState(false);
  const [onDuty, setOnDuty] = useState([]);

  const load = async () => { const { data } = await api.get("/queue"); setQueue(data || []); };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!onDutyOpen) return;
    (async () => {
      try {
        const { data } = await api.get("/staff");
        const items = (data || []).filter((s) => (s.availability || "in_clinic") !== "off" && ["doctor", "nurse", "lab_tech", "pharmacist", "radiologist"].includes(s.role));
        setOnDuty(items);
      } catch { setOnDuty([]); }
    })();
  }, [onDutyOpen]);

  const today = new Date().toISOString().slice(0, 10);
  const waiting = queue.filter((q) => q.status === "Waiting");
  const untriaged = queue.filter((q) => q.status === "Waiting" && !q.acuity);
  const triagedToday = queue.filter((q) => q.acuity && (q.triaged_at || q.created_at || "").slice(0, 10) === today);
  const critical = queue.filter((q) => q.acuity && q.acuity <= 2);

  // Clicking an active card = toggle back to "all"
  const applyFilter = (next) => {
    setTab("triage");
    setTriageFilter((cur) => (cur === next ? "all" : next));
  };

  const filteredTriage = triageFilter === "waiting"  ? untriaged
                       : triageFilter === "today"    ? triagedToday
                       : triageFilter === "critical" ? critical
                       : queue;

  const activeCls = "ring-2 ring-[#1976D2] ring-offset-1";

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="nurse-page">
      <PageHeader title="Nursing & Triage" subtitle="Triage acuity, medication administration and intake/output records." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="k-awaiting-triage" onClick={() => applyFilter("waiting")}  className={triageFilter === "waiting"  && tab === "triage" ? activeCls : ""} label="Awaiting triage"      value={untriaged.length}     icon={HeartPulse}    tone="amber" hint="Not yet triaged" />
        <KpiCard testId="k-triaged-today"   onClick={() => applyFilter("today")}    className={triageFilter === "today"    && tab === "triage" ? activeCls : ""} label="Triaged today"        value={triagedToday.length}  icon={ClipboardList} tone="blue"  hint="Triaged with today's date" />
        <KpiCard testId="k-critical"        onClick={() => applyFilter("critical")} className={triageFilter === "critical" && tab === "triage" ? activeCls : ""} label="Acuity 1/2 (critical)" value={critical.length}      icon={HeartPulse}    tone="red"   hint="ESI 1 & 2 only" />
        <div className="relative">
          <KpiCard testId="k-on-duty" onClick={() => setOnDutyOpen((v) => !v)} className={onDutyOpen ? activeCls : ""} label="On duty" value={"View roster"} icon={Activity} tone="slate" hint="Click for shift roster" />
          {onDutyOpen && (
            <div className="absolute z-30 right-0 mt-2 w-80 rounded-xl bg-white border border-slate-200 shadow-lg p-3" data-testid="on-duty-popover">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Currently on duty · {onDuty.length}</div>
                <button onClick={() => setOnDutyOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              {onDuty.length === 0 ? <div className="text-xs text-slate-500 p-2">No staff marked available.</div> : (
                <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100 text-xs">
                  {onDuty.map((s) => (
                    <li key={s.user_id} className="py-1.5 flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-slate-800">{s.name}</div>
                        <div className="text-[10px] text-slate-500">{s.specialty || s.department || s.role}</div>
                      </div>
                      <div className="text-right">
                        <Chip tone={s.role === "doctor" ? "blue" : s.role === "nurse" ? "amber" : "slate"}>{s.role.replace("_"," ")}</Chip>
                        <div className="text-[10px] text-slate-500 mt-0.5">{(s.availability || "in_clinic").replace("_"," ")}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-slate-200 flex gap-1">
        {[["rounds","Vitals rounds"],["triage","Triage queue"],["mar","MAR (per patient)"],["io","Intake / Output"]].map(([k,l])=>(
          <button key={k} data-testid={`nurse-tab-${k}`} onClick={()=>{ setTab(k); if (k !== "triage") setTriageFilter("all"); }} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab===k?"border-[#1976D2] text-[#1565C0]":"border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "rounds" && <NurseRounds />}

      {tab === "triage" && (
        <SectionCard
          title={
            triageFilter === "all" ? "Triage queue"
            : triageFilter === "waiting" ? `Awaiting triage · ${untriaged.length}`
            : triageFilter === "today" ? `Triaged today · ${triagedToday.length}`
            : `Critical · Acuity 1/2 · ${critical.length}`
          }
          actions={triageFilter !== "all" && (
            <button data-testid="triage-clear-filter" onClick={() => setTriageFilter("all")} className="h-8 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-medium flex items-center gap-1">
              <X className="w-3 h-3" /> Show all
            </button>
          )}
        >
          {filteredTriage.length === 0 ? <EmptyState icon={HeartPulse} title="Nothing here" /> : (
            <ul className="divide-y divide-slate-100">
              {filteredTriage.map((q) => (
                <li key={q.queue_id} className="p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`triage-row-${q.queue_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-semibold">#{q.queue_no}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{q.patient_name}</div>
                      <div className="text-xs text-slate-500 tabular">{q.patient_ths_id} · {q.department}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {q.acuity ? <Chip tone={ACUITY[q.acuity].tone}>{ACUITY[q.acuity].label}</Chip> : <StatusPill status={q.priority === "Urgent" ? "critical" : "normal"} />}
                    <button data-testid={`triage-${q.queue_id}`} onClick={() => setTriageFor(q)} className="h-8 px-3 rounded-lg bg-[#1976D2] text-white text-xs font-semibold hover:bg-[#1565C0]">Triage</button>
                    <button onClick={() => navigate(`/app/patients/${q.patient_id}`)} className="text-slate-400 hover:text-slate-700"><ArrowRight className="w-4 h-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      )}

      {tab === "mar" && <PatientPicker title="Select a patient to view MAR" onPick={(p) => navigate(`/app/patients/${p.patient_id}`)} hint="Open the patient chart to record medication administration and view MAR history." />}

      {tab === "io" && <PatientPicker title="Select a patient to record I&O" onPick={(p) => navigate(`/app/patients/${p.patient_id}`)} hint="Open the patient chart to log intake/output and see the running fluid balance." />}

      {triageFor && <TriageModal q={triageFor} onClose={() => setTriageFor(null)} onDone={() => { setTriageFor(null); load(); }} />}
    </div>
  );
}

function TriageModal({ q, onClose, onDone }) {
  const [acuity, setAcuity] = useState(3);
  const [complaint, setComplaint] = useState("");
  const [notes, setNotes] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [workload, setWorkload] = useState({}); // user_id -> {waiting, seen_today}
  const [assignDoctorId, setAssignDoctorId] = useState(q.assigned_doctor_id || "");

  useEffect(() => {
    (async () => {
      try {
        const [docs, wl] = await Promise.all([
          api.get("/staff?role=doctor"),
          api.get("/staff/doctor-workload"),
        ]);
        setDoctors(docs.data || []);
        const map = {};
        (wl.data || []).forEach((d) => { map[d.user_id] = d; });
        setWorkload(map);
        // Auto-suggest: least-loaded, but skip anyone marked off
        const pickable = (wl.data || []).filter((d) => (d.availability || "in_clinic") !== "off");
        if (!q.assigned_doctor_id && pickable.length > 0) {
          setAssignDoctorId(pickable[0].user_id);
        }
      } catch { setDoctors([]); }
    })();
  }, []); // eslint-disable-line

  const suggestedId = workload && Object.keys(workload).length > 0
    ? [...doctors].sort((a, b) => ((workload[a.user_id]?.waiting ?? 99) - (workload[b.user_id]?.waiting ?? 99)))[0]?.user_id
    : null;

  const submit = async () => {
    if (!complaint.trim()) return toast.error("Chief complaint is required");
    try {
      await api.post("/triage", { patient_id: q.patient_id, queue_id: q.queue_id, acuity, chief_complaint: complaint, notes });
      if (assignDoctorId) {
        try { await api.post(`/queue/${q.queue_id}/assign`, { doctor_id: assignDoctorId }); }
        catch (err) { toast.error(err?.response?.data?.detail || "Assigned failed"); }
      }
      toast.success(`Triaged as ${ACUITY[acuity].label}${assignDoctorId ? " · doctor assigned" : ""}`);
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : "Failed to triage");
      toast.error(msg);
      console.error("Triage failed:", err?.response?.status, detail || err);
    }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="triage-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm text-slate-500">Triage</div>
            <div className="font-semibold">{q.patient_name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <label className="block mb-3">
          <span className="text-[11px] uppercase text-slate-500 font-semibold">Acuity level (ESI 1–5)</span>
          <div className="mt-1 grid grid-cols-5 gap-1">
            {[1,2,3,4,5].map((n) => (
              <button key={n} data-testid={`acuity-${n}`} onClick={() => setAcuity(n)} className={`h-10 rounded-lg text-sm font-semibold border ${acuity===n?"border-[#1976D2] bg-[#e6f0fb] text-[#1565C0]":"border-slate-200 hover:bg-slate-50 text-slate-700"}`}>{n}</button>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-1">{ACUITY[acuity].label}</div>
        </label>
        <label className="block mb-3">
          <span className="text-[11px] uppercase text-slate-500 font-semibold">Chief complaint *</span>
          <input data-testid="triage-complaint" value={complaint} onChange={(e) => setComplaint(e.target.value)} placeholder="e.g. Fever + severe headache" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <label className="block mb-3">
          <span className="text-[11px] uppercase text-slate-500 font-semibold">Assign to doctor</span>
          <select data-testid="triage-assign-doctor" value={assignDoctorId} onChange={(e) => setAssignDoctorId(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">— Route to next available —</option>
            {(() => {
              const groups = {};
              doctors.forEach((d) => {
                const k = d.specialty || "General";
                groups[k] = groups[k] || []; groups[k].push(d);
              });
              return Object.entries(groups).map(([spec, list]) => (
                <optgroup key={spec} label={spec}>
                  {list.map((d) => {
                    const w = workload[d.user_id];
                    const avail = d.availability || w?.availability || "in_clinic";
                    const availTag = avail === "off" ? " · OFF" : avail === "on_call" ? " · on-call" : "";
                    const suffix = w ? `  · ${w.waiting} waiting · ${w.seen_today} today` : "";
                    const suggested = d.user_id === suggestedId ? "  ⭐" : "";
                    return <option key={d.user_id} value={d.user_id} disabled={avail === "off"}>{d.name}{availTag}{suffix}{suggested}</option>;
                  })}
                </optgroup>
              ));
            })()}
          </select>
          {suggestedId && assignDoctorId === suggestedId && (
            <div className="text-[11px] text-[#1B5E20] mt-1" data-testid="triage-suggested">Auto-suggested: least-loaded doctor</div>
          )}
          {q.assigned_doctor_name && !assignDoctorId && <div className="text-[11px] text-slate-500 mt-1">Currently assigned: {q.assigned_doctor_name}</div>}
        </label>
        <label className="block mb-3">
          <span className="text-[11px] uppercase text-slate-500 font-semibold">Notes</span>
          <textarea data-testid="triage-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="triage-submit" onClick={submit} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Save triage</button>
        </div>
      </div>
    </div>
  );
}

function PatientPicker({ title, onPick, hint }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let t = setTimeout(async () => {
      if (q.length < 2) { setRows([]); return; }
      const { data } = await api.get(`/patients?q=${encodeURIComponent(q)}&limit=10`);
      setRows(data || []);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <SectionCard title={title}>
      <div className="p-5">
        <div className="text-sm text-slate-500 mb-3">{hint}</div>
        <input data-testid="nurse-patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, Patient ID, phone…" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <div className="mt-2 divide-y divide-slate-100">
          {rows.map((p) => (
            <button key={p.patient_id} onClick={() => onPick(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 rounded" data-testid={`nurse-pick-${p.patient_id}`}>
              <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
              <div className="text-xs text-slate-500 tabular">{p.ths_id} · {p.phone_primary}</div>
            </button>
          ))}
        </div>
      </div>
    </SectionCard>
  );
}
